/**
 * Golf Genius Cross Product Navigation (CPN) helpers.
 *
 * Loads the IdP-hosted `gg_menu.js` script once (it defines `window.GgMenu`)
 * and exposes a helper to open the menu. Mirrors the one-off script-injection
 * pattern used in `calendly-widget.ts`.
 */

// The gem's menu init logic requires the script tag to carry this id.
export const GG_MENU_SCRIPT_ID = "gg-menu-script";

let scriptPromise: Promise<void> | null = null;

export function loadGgMenuScript(src: string): Promise<void> {
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(GG_MENU_SCRIPT_ID);
    const existingGg = (window as unknown as { GgMenu?: GgMenuGlobal }).GgMenu;

    // If a previous load failed, the script tag may still exist. Only treat the
    // script as "loaded" when it actually defined window.GgMenu.
    if (existing) {
      if (existingGg && typeof existingGg.showMenu === "function") {
        resolve();
        return;
      }
      existing.remove();
    }

    const script = document.createElement("script");
    script.id = GG_MENU_SCRIPT_ID;
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null; // allow a later retry
      script.remove();
      reject(new Error("Failed to load Golf Genius menu script"));
    };

    const first = document.getElementsByTagName("script")[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
}

interface GgMenuGlobal {
  showMenu: (data: unknown) => void;
}

/** Opens the Golf Genius menu. Returns false if the script/data isn't ready. */
export function showGgMenu(menuData: unknown): boolean {
  const gg = (window as unknown as { GgMenu?: GgMenuGlobal }).GgMenu;

  if (gg && typeof gg.showMenu === "function" && menuData != null) {
    gg.showMenu(menuData);
    return true;
  }

  return false;
}
